var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect } from 'react';
import { FaTimes, FaCloudUploadAlt, FaCheck, FaExclamationTriangle, FaTrash } from 'react-icons/fa';
import './EntityBulkUploadModal.scss';
import { createPortal } from 'react-dom';
const formatFileSize = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
};
const loadImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
    });
};
export const EntityBulkUploadModal = ({ isOpen, onClose, levelId, onUploaded, title, upload, uploadView, resultView, }) => {
    const fileInputRef = useRef(null);
    const modalBodyRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [stage, setStage] = useState('select');
    const [dragOver, setDragOver] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    // Scroll to top when stage changes
    useEffect(() => {
        if (modalBodyRef.current)
            modalBodyRef.current.scrollTop = 0;
    }, [stage]);
    if (!isOpen)
        return null;
    const addFiles = (fileList) => __awaiter(void 0, void 0, void 0, function* () {
        if (!fileList)
            return;
        const arr = [];
        for (const file of Array.from(fileList)) {
            try {
                const dimensions = yield loadImageDimensions(file);
                arr.push({
                    file,
                    previewUrl: URL.createObjectURL(file),
                    status: 'pending',
                    progress: 0,
                    dimensions,
                });
            }
            catch (_a) {
                // If dimensions loading fails, still add the file without dimensions
                arr.push({
                    file,
                    previewUrl: URL.createObjectURL(file),
                    status: 'pending',
                    progress: 0,
                });
            }
        }
        setFiles(prev => [...prev, ...arr]);
    });
    const removeFile = (fileToRemove) => {
        setFiles(prev => prev.filter(f => f.file !== fileToRemove));
    };
    const handleFileInputChange = (e) => {
        addFiles(e.target.files);
        e.target.value = '';
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };
    const handleDropZone = (e) => {
        e.preventDefault();
        setDragOver(false);
        addFiles(e.dataTransfer.files);
    };
    const startUpload = () => __awaiter(void 0, void 0, void 0, function* () {
        setStage('upload');
        const totalFiles = files.length;
        let completedFiles = 0;
        for (const item of files) {
            const newItem = Object.assign(Object.assign({}, item), { status: 'uploading', progress: 0 });
            setFiles(prev => prev.map(fi => fi === item ? newItem : fi));
            try {
                const resp = yield upload({ levelId, file: item.file });
                if (resp.success) {
                    newItem.status = 'done';
                }
                else {
                    throw new Error(resp.error || 'Upload failed');
                }
            }
            catch (err) {
                newItem.status = 'error';
                newItem.errorMsg = err.message;
            }
            newItem.progress = 100;
            completedFiles++;
            setUploadProgress((completedFiles / totalFiles) * 100);
            setFiles(prev => prev.map(fi => fi.file === newItem.file ? newItem : fi));
        }
        setStage('result');
        onUploaded();
    });
    const getNextButtonText = () => {
        if (stage === 'select')
            return 'Upload';
        if (stage === 'upload')
            return 'Uploading...';
        return 'Close';
    };
    const isNextDisabled = () => {
        if (stage === 'select')
            return files.length === 0;
        if (stage === 'upload')
            return true;
        return false;
    };
    const handleNext = () => {
        if (stage === 'select')
            startUpload();
        else if (stage === 'result')
            onClose();
    };
    const renderUploadStage = () => {
        if (uploadView === 'compact') {
            return (_jsxs("div", { className: "upload-progress-container", children: [_jsx("div", { className: "progress-bar", children: _jsx("div", { className: "progress-fill", style: { width: `${uploadProgress}%` } }) }), _jsxs("div", { className: "progress-text", children: ["Uploading ", Math.round(uploadProgress), "%"] })] }));
        }
        return (_jsxs("div", { className: "upload-progress-container", children: [_jsx("h4", { children: "Uploading files..." }), _jsx("div", { className: "progress-bar", children: _jsx("div", { className: "progress-fill", style: { width: `${uploadProgress}%` } }) }), _jsxs("p", { children: [Math.round(uploadProgress), "% complete"] }), uploadView === 'fileList' && (_jsx("div", { className: "upload-files-list", children: files.map((fileItem, index) => (_jsxs("div", { className: "upload-file-item", children: [_jsx("span", { className: "file-name", children: fileItem.file.name }), _jsxs("div", { className: "file-status", children: [fileItem.status === 'uploading' && _jsx("span", { className: "status-uploading", children: "Uploading..." }), fileItem.status === 'done' && _jsx(FaCheck, { className: "status-done" }), fileItem.status === 'error' && _jsx(FaExclamationTriangle, { className: "status-error" })] })] }, `${fileItem.file.name}-${index}`))) }))] }));
    };
    const renderResultStage = () => {
        if (resultView === 'fileList') {
            return (_jsxs("div", { className: "result-container", children: [_jsx("h3", { children: "Upload complete" }), _jsx("ul", { className: "result-list", children: files.map((fileItem, index) => (_jsxs("li", { className: fileItem.status, children: [fileItem.status === 'done' && _jsx(FaCheck, { className: "status-icon success" }), " ", fileItem.status === 'error' && _jsx(FaExclamationTriangle, { className: "status-icon error" }), fileItem.file.name] }, `${fileItem.file.name}-${index}`))) })] }));
        }
        const successCount = files.filter(f => f.status === 'done').length;
        const errorCount = files.filter(f => f.status === 'error').length;
        return (_jsxs("div", { className: "result-container", children: [_jsx("h4", { children: "Upload Complete" }), _jsxs("div", { className: "result-summary", children: [_jsxs("div", { className: "result-stat success", children: [_jsx(FaCheck, {}), " ", successCount, " uploaded successfully"] }), errorCount > 0 && (_jsxs("div", { className: "result-stat error", children: [_jsx(FaExclamationTriangle, {}), " ", errorCount, " failed"] }))] }), resultView === 'summaryWithErrors' && errorCount > 0 && (_jsxs("div", { className: "error-files", children: [_jsx("h5", { children: "Failed uploads:" }), files.filter(f => f.status === 'error').map((fileItem, index) => (_jsxs("div", { className: "error-file", children: [_jsx("span", { className: "file-name", children: fileItem.file.name }), _jsx("span", { className: "error-msg", children: fileItem.errorMsg })] }, index)))] }))] }));
    };
    const renderContent = () => {
        if (stage === 'select') {
            return (_jsxs("div", { children: [_jsxs("div", { className: `drop-zone ${dragOver ? 'drag-over' : ''}`, onClick: () => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDropZone, children: [_jsx(FaCloudUploadAlt, { className: "drop-icon" }), _jsx("div", { className: "drop-text", children: "Drop images here or click to select" }), _jsx("div", { className: "drop-hint", children: "Supports JPG, PNG, GIF formats" }), _jsx("input", { type: "file", multiple: true, accept: "image/*", ref: fileInputRef, onChange: handleFileInputChange, style: { display: 'none' } })] }), files.length > 0 && (_jsx("div", { className: "files-table-container", children: _jsxs("table", { className: "files-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "File Name" }), _jsx("th", { children: "Dimensions" }), _jsx("th", { children: "Size" }), _jsx("th", { children: "Actions" })] }) }), _jsx("tbody", { children: files.map((fileItem, index) => (_jsxs("tr", { children: [_jsx("td", { className: "file-name", children: fileItem.file.name }), _jsx("td", { className: "file-dimensions", children: fileItem.dimensions ? `${fileItem.dimensions.width}×${fileItem.dimensions.height}px` : 'Loading...' }), _jsx("td", { className: "file-size", children: formatFileSize(fileItem.file.size) }), _jsx("td", { className: "file-actions", children: _jsx("button", { className: "delete-button", onClick: () => removeFile(fileItem.file), title: "Remove file", children: _jsx(FaTrash, {}) }) })] }, `${fileItem.file.name}-${index}`))) })] }) }))] }));
        }
        if (stage === 'upload')
            return renderUploadStage();
        return renderResultStage();
    };
    return createPortal(_jsx("div", { className: "modal-overlay", children: _jsxs("div", { className: "bulk-modal", children: [_jsxs("div", { className: "modal-header", children: [_jsx("h3", { children: title }), _jsx("button", { className: "close-button", onClick: onClose, children: _jsx(FaTimes, {}) })] }), _jsx("div", { className: "modal-body", ref: modalBodyRef, children: renderContent() }), _jsx("div", { className: "modal-footer", children: _jsx("button", { className: "next-button", onClick: handleNext, disabled: isNextDisabled(), children: getNextButtonText() }) })] }) }), document.body);
};
export default EntityBulkUploadModal;
